/**
 * OTP Formatters Utility
 * Handles data formatting for OTP displays
 */

import { format } from 'date-fns';
import { shortDate, longDate, getBrowserLocaleFormat } from '@/helpers/dateFormatter';
import { intToDate, intToTimeStr, localFormatTimeString } from '@/helpers/dateTime';
import { escapeHtml } from '@/helpers/string';
import { formatXAxisLabel } from '../../../../helpers/chartXAxisFormatter';

/**
 * Format date range based on locale using shortDate helper
 * @param {string|Object} dateRange - Date range object or string
 * @returns {string} Formatted date range
 */
export function formatDateRange(dateRange) {
  if (!dateRange) return '';

  if (typeof dateRange === 'string') {
    return shortDate(dateRange, '');
  }

  if (dateRange.fromDate && dateRange.toDate) {
    const from = shortDate(dateRange.fromDate, '');
    const to = shortDate(dateRange.toDate, '');
    return `${from} - ${to}`;
  }

  return '';
}

/**
 * Clock time an hour bucket starts at, following the browser locale like every other time on
 * the dashboard ("6:00 AM" for en-US, "06:00" otherwise).
 * @param {number} hour - Hour of day (0-23)
 * @returns {string} Formatted time
 */
function formatBucketHour(hour) {
  return localFormatTimeString(intToTimeStr(hour * 100));
}

/**
 * X-axis label of a by-date chart bucket (see generateDateBuckets in the otpDashboard store):
 * the hour, the day, the week ("Sep 21-27") or the month ("Sep 2026"). Hours and days follow the
 * browser locale like the Ridership chart beside it ("6:00 AM" and "9/24" for en-US, "06:00"
 * and "24/9" otherwise).
 * @param {Object} bucket - Bucket with period, date and hour
 * @param {string} locale - UI locale, for month names
 * @returns {string} Axis label
 */
export function formatOtpBucketLabel(bucket, locale) {
  if (!bucket?.period) return '';
  if (bucket.period === 'hourly') return formatBucketHour(bucket.hour);
  if (bucket.period === 'daily') return format(intToDate(bucket.date), getBrowserLocaleFormat({ type: 'dateShort' }));
  return formatXAxisLabel({ operationDay: bucket.date }, bucket.period, locale);
}

/**
 * Tooltip heading of a by-date chart bucket. Unlike the axis label it names the exact span:
 * the day and hour on the hourly view, and on the weekly and monthly views the selected days
 * the bucket covers, so a week or month cut by the range edges does not claim days it lacks.
 * @param {Object} bucket - Bucket with period, date, hour, start and end
 * @param {string} [emptyValue=''] - Returned when the bucket has no date
 * @returns {string} Tooltip heading
 */
export function formatOtpBucketTitle(bucket, emptyValue = '') {
  if (!bucket?.date) return emptyValue;
  const { period, date, hour, start, end } = bucket;

  if (period === 'hourly') {
    return `${longDate(date, emptyValue)}, ${formatBucketHour(hour)} - ${formatBucketHour((hour + 1) % 24)}`;
  }
  if (period === 'weekly' || period === 'monthly') {
    return start === end ? longDate(start, emptyValue) : formatDateRange({ fromDate: start, toDate: end });
  }
  return longDate(date, emptyValue);
}

/**
 * Format percentage value
 * @param {number} value - Percentage value
 * @param {number} decimals - Number of decimal places
 * @returns {string} Formatted percentage
 */
export function formatPercentage(value, decimals = 1) {
  if (value === null || value === undefined) return '0';
  return Number(value).toFixed(decimals);
}

/**
 * Format tooltip data for display
 * @param {Object} data - Raw data object
 * @returns {Object} Formatted data object
 */
export function formatTooltipData(data) {
  if (!data) return null;

  const formatted = { ...data };

  // Format percentages
  if (formatted.latePercentage !== undefined) {
    formatted.latePercentage = formatPercentage(formatted.latePercentage);
  }
  if (formatted.earlyPercentage !== undefined) {
    formatted.earlyPercentage = formatPercentage(formatted.earlyPercentage);
  }
  if (formatted.skippedPercentage !== undefined) {
    formatted.skippedPercentage = formatPercentage(formatted.skippedPercentage);
  }
  if (formatted.otpPercentage !== undefined) {
    formatted.otpPercentage = formatPercentage(formatted.otpPercentage);
  }

  return formatted;
}

/**
 * Format duration in minutes
 * @param {number} minutes - Duration in minutes
 * @returns {string} Formatted duration
 */
export function formatDuration(minutes) {
  if (!minutes) return '0 min';
  return `${Math.abs(minutes)} min`;
}

/**
 * Format number with locale
 * @param {number} value - Number value
 * @param {string} locale - Locale code
 * @returns {string} Formatted number
 */
export function formatNumber(value, locale = 'en-US') {
  if (value === null || value === undefined) return '0';
  return new Intl.NumberFormat(locale).format(value);
}

/**
 * Truncate text with ellipsis
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum length
 * @returns {string} Truncated text
 */
export function truncateText(text, maxLength = 50) {
  if (!text || text.length <= maxLength) return text;
  return `${text.substring(0, maxLength)}...`;
}

/**
 * Format route name for display
 * @param {string} routeName - Route name
 * @param {number} maxLength - Maximum length
 * @returns {string} Formatted route name
 */
export function formatRouteName(routeName, maxLength = 50) {
  return truncateText(routeName, maxLength);
}

/**
 * Format stop name for display
 * @param {string} stopName - Stop name
 * @param {number} maxLength - Maximum length
 * @returns {string} Formatted stop name
 */
export function formatStopName(stopName, maxLength = 50) {
  return truncateText(stopName, maxLength);
}

/**
 * Generate HTML for route tooltip
 * @param {Object} data - Route data
 * @param {Function} t - Translation function
 * @returns {string} HTML string for tooltip
 */
export function generateRouteTooltipHTML(data, t) {
  if (!data) return '';

  const dateRangeStr = data.dateRange ? formatDateRange(data.dateRange) : '';

  return `
    <div class="otp-tooltip-content">
      <div class="tooltip-header">
        <strong>${data.routeName || ''}</strong>
      </div>
      ${dateRangeStr ? `<div class="tooltip-subheader">${dateRangeStr}</div>` : ''}
      <div class="tooltip-body">
        <div class="tooltip-row">
          <span class="label-row">${t('flexigo.otp.totalStops')}:</span>
          <span class="value">${data.totalStops || 0}</span>
        </div>
        <div class="tooltip-row">
          <span class="label-row">${t('flexigo.otp.lateArrival')}:</span>
          <span class="value">${data.lateCount || 0} (${data.late || 0}%)</span>
        </div>
        <div class="tooltip-row">
          <span class="label-row">${t('flexigo.otp.earlyDeparture')}:</span>
          <span class="value">${data.earlyCount || 0} (${data.early || 0}%)</span>
        </div>
        <div class="tooltip-row">
          <span class="label-row">${t('flexigo.otp.skipped')}:</span>
          <span class="value">${data.skippedCount || 0} (${data.skipped || 0}%)</span>
        </div>
        <div class="tooltip-row">
          <span class="label-row">${t('flexigo.otp.leftBehind')}:</span>
          <span class="value">${data.leftBehind || 0}</span>
        </div>
        <div class="tooltip-row highlight">
          <span class="label-row">${t('flexigo.otp.onTimePerformance')}:</span>
          <span class="value">${data.onTime || 0}%</span>
        </div>
      </div>
    </div>
  `;
}

/** Routes the left-behind tooltip lists by name before summing the rest into one row. */
const LEFT_BEHIND_TOOLTIP_ROUTES = 10;

/**
 * Generate HTML for left behind tooltip
 * @param {Object} data - Left behind data
 * @param {Function} t - Translation function
 * @returns {string} HTML string for tooltip
 */
export function generateLeftBehindTooltipHTML(data, t) {
  if (!data) return '';

  const dateStr = formatOtpBucketTitle(data);

  // A disc stands for a whole chart bucket (an hour, day, week or month), so it lists the routes
  // behind its number — the bucket total alone leaves no way to tell where the riders were left
  // (VSER-14676). The store hands out one event per trip and stop, so a route that left riders on
  // several days of a week or month is summed into one row; the rows run largest first, and past
  // LEFT_BEHIND_TOOLTIP_ROUTES the rest are summed into a single "other routes" row so the
  // tooltip stays readable.
  const byRoute = new Map();
  (data.events || []).forEach((event) => {
    if (!(event.count > 0)) return;
    byRoute.set(event.routeName, (byRoute.get(event.routeName) || 0) + event.count);
  });
  const routes = [...byRoute].sort(([, a], [, b]) => b - a);
  const shown = routes.slice(0, LEFT_BEHIND_TOOLTIP_ROUTES);
  const rest = routes.slice(LEFT_BEHIND_TOOLTIP_ROUTES);

  const row = (label, count) => `
        <div class="tooltip-row">
          <span class="label-row">${label}</span>
          <span class="value">${count}</span>
        </div>`;
  const events = [
    ...shown.map(([routeName, count]) => row(escapeHtml(routeName), count)),
    ...(rest.length
      ? [
          row(
            t('flexigo.otp.otherRoutes', { count: rest.length }),
            rest.reduce((sum, [, count]) => sum + count, 0),
          ),
        ]
      : []),
  ].join('');

  // With a breakdown above it the total becomes the summary row, set apart like the one on
  // the route tooltip; on its own it is just the single row.
  const totalClass = events ? 'tooltip-row highlight' : 'tooltip-row';

  return `
    <div class="otp-tooltip-content">
      <div class="tooltip-header">
        <strong>${dateStr}</strong>
      </div>
      <div class="tooltip-body otp-tooltip-breakdown">${events}
        <div class="${totalClass}">
          <span class="label-row">${t('flexigo.otp.leftBehind')} (ED/Skipped):</span>
          <span class="value">${data.count || 0} ${t('flexigo.otp.riders')}</span>
        </div>
      </div>
    </div>
  `;
}

/**
 * Position tooltip intelligently next to data point
 * @param {number} labelWidth - Tooltip width
 * @param {number} labelHeight - Tooltip height
 * @param {Object} point - Point data with plotX, plotY
 * @param {Object} chart - Highcharts chart instance
 * @returns {Object} Position with x and y coordinates
 */
export function positionOtpTooltip(labelWidth, labelHeight, point, chart) {
  const { plotLeft, plotWidth, plotTop, plotHeight } = chart;
  const pointX = point.plotX + plotLeft;
  const pointY = point.plotY + plotTop;

  // Check if there's enough space on the right side
  const spaceOnRight = plotLeft + plotWidth - pointX;
  const spaceOnLeft = pointX - plotLeft;

  let x;
  if (spaceOnRight >= labelWidth + 20) {
    // Position to the right of the point
    x = pointX + 10;
  } else if (spaceOnLeft >= labelWidth + 20) {
    // Position to the left of the point
    x = pointX - labelWidth - 10;
  } else {
    // Default to left of point if neither side has enough space
    x = pointX - labelWidth - 10;
  }

  // Calculate Y position, centered on point but constrained to plot area
  let y = pointY - labelHeight / 2;

  // Ensure tooltip doesn't go above plot area
  if (y < plotTop) {
    y = plotTop + 5;
  }

  // Ensure tooltip doesn't go below plot area
  if (y + labelHeight > plotTop + plotHeight) {
    y = plotTop + plotHeight - labelHeight - 5;
  }

  return {
    x: Math.max(plotLeft, Math.min(x, plotLeft + plotWidth - labelWidth)),
    y,
  };
}

/**
 * Generate HTML for date-based OTP tooltip (for CombinedOtpChart)
 * @param {Object} data - Date data with stats
 * @param {Function} t - Translation function
 * @returns {string} HTML string for tooltip
 */
export function generateDateTooltipHTML(data, t) {
  if (!data) return '';

  // A by-date chart bucket passes its heading in; otherwise the date is the heading.
  const dateStr = data.title || (data.date ? longDate(data.date, String(data.date)) : '');

  const stopsAndPercent = (count, percentage) =>
    `${formatNumber(count || 0)} ${t('flexigo.otp.stops')} - ${percentage || 0}%`;
  const mins = (value) => `${formatNumber(value || 0)} ${t('flexigo.otp.mins')}`;

  const rows = [
    [t('flexigo.otp.lateArrival'), stopsAndPercent(data.lateCount, data.late)],
    [t('flexigo.otp.earlyDeparture'), stopsAndPercent(data.earlyCount, data.early)],
    [t('flexigo.otp.skipped'), stopsAndPercent(data.skippedCount, data.skipped)],
    [t('flexigo.otp.maxLateDeviation'), mins(data.maxLate)],
    [t('flexigo.otp.maxEarlyDeviation'), mins(data.maxEarly)],
    [t('flexigo.otp.onTimePerformance'), `${data.otpPercentage || 0}%`, 'highlight'],
  ];

  // `otp-tooltip-pairs` lays the rows out as one grid, so every value starts just past the
  // longest label rather than each row placing its own value (see otpTooltip.css).
  return `
    <div class="otp-tooltip-content" style="margin: 10px;">
      <div class="tooltip-header mb-3 fw-bold">
        <strong>${dateStr}</strong>
      </div>
      <div class="otp-tooltip-pairs">
        ${rows
          .map(
            ([label, value, className = '']) => `<div class="tooltip-row ${className}">
          <span class="label-row fw-normal">${label}:</span>
          <span class="value fw-bold">${value}</span>
        </div>`,
          )
          .join('')}
      </div>
    </div>
  `;
}

/**
 * Format OTP data for date-based tooltip
 * @param {string|number} bucketKey - Category key of the hovered bucket (its `key`)
 * @param {Array} lateArrivalsData - Late arrivals data array (for max deviation)
 * @param {Array} earlyDeparturesData - Early departures data array (for max deviation)
 * @param {Array} otpPercentagesData - OTP percentages data array
 * @param {Array} dateStatsData - Date stats data array (for actual counts and percentages)
 * @param {Function} t - Translation function
 * @returns {string} HTML string for tooltip
 */
export function formatOtpDateTooltip(
  bucketKey,
  lateArrivalsData,
  earlyDeparturesData,
  otpPercentagesData,
  dateStatsData,
  t,
) {
  // Find the corresponding data from the arrays
  const lateArrival = lateArrivalsData?.find((item) => item.key === bucketKey);
  const earlyDeparture = earlyDeparturesData?.find((item) => item.key === bucketKey);
  const otpPercentage = otpPercentagesData?.find((item) => item.key === bucketKey);
  const dateStats = dateStatsData?.find((item) => item.key === bucketKey);

  if (!lateArrival && !earlyDeparture && !otpPercentage && !dateStats) {
    return '';
  }

  const title = formatOtpBucketTitle(dateStats, String(dateStats?.date ?? ''));

  // Empty bucket (no trips in the selected filters) -> show a simple "no trips" note
  if (dateStats?.isEmpty) {
    const noTripsKey = dateStats.period === 'daily' ? 'flexigo.otp.noTripsOnDate' : 'flexigo.otp.noTripsInPeriod';
    return `
      <div class="otp-tooltip-content" style="margin: 10px;">
        <div class="tooltip-header mb-2 fw-bold">
          <strong>${title}</strong>
        </div>
        <div class="vstack gap-1">
          <span class="fw-normal">${t(noTripsKey)}</span>
        </div>
      </div>
    `;
  }

  // Use actual stats from dateStats instead of box plot counts
  const stats = dateStats?.stats || {};
  const lateCount = stats.lateCount || 0;
  const earlyCount = stats.earlyCount || 0;
  const skippedCount = stats.skippedCount || 0;
  const latePercentage = stats.late || 0;
  const earlyPercentage = stats.early || 0;
  const skippedPercentage = stats.skipped || 0;

  const tooltipData = {
    title,
    lateCount,
    late: latePercentage,
    earlyCount,
    early: earlyPercentage,
    skippedCount,
    skipped: skippedPercentage,
    maxLate: lateArrival?.max || 0,
    maxEarly: earlyDeparture?.max || 0,
    otpPercentage: otpPercentage?.percentage || 0,
  };

  return generateDateTooltipHTML(tooltipData, t);
}
